import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { apiService } from '../../services/api';
import SuccessModal from './StaffOnboardSuccessModal';
import { useNavigate } from 'react-router-dom';

const EmployeeOnboarding = () => {
    const { state } = useAuth();
    const token = state.token;  

  // State for form inputs
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    joiningDate: '',
    baseSalary: '',
    department: '',
    customDepartment: '',
    position: '',
    allowances: [
      { type: 'Housing', amount: 0 },
      { type: 'Transport', amount: 0 },
      { type: 'Medical', amount: 0 }
    ],
    bonuses: [
      { type: 'Performance', amount: 0 },
      { type: 'Year-End', amount: 0 }
    ],
    deductions: [
      { type: 'Tax', amount: 0 },
      { type: 'Pension', amount: 0 },
      { type: 'Health Insurance', amount: 0 }
    ]
  });

  // Options for dropdowns
  const allowanceOptions = ['Housing', 'Transport', 'Medical', 'Meal', 'Communication', 'Utilities', 'Education'];
  const bonusOptions = ['Performance', 'Year-End', 'Signing', 'Retention', 'Project Completion', 'Referral'];
  const deductionOptions = ['Tax', 'Pension', 'Health Insurance', 'Loan Repayment', 'Voluntary Savings'];
  const departmentOptions = ['Marketing', 'Sales', 'Engineering', 'Human Resources', 'Finance', 'Customer Support', 'Product', 'Research & Development'];

  // State for custom dropdown menus
  const [dropdowns, setDropdowns] = useState({
    department: false,
    allowanceType: false,
    bonusType: false,
    deductionType: false
  });

  // State for validation
  const [errors, setErrors] = useState({});

  // State for current step in multi-step form
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  // State for new custom fields
  const [newAllowance, setNewAllowance] = useState({ type: '', amount: 0 });
  const [newBonus, setNewBonus] = useState({ type: '', amount: 0 });
  const [newDeduction, setNewDeduction] = useState({ type: '', amount: 0 });

  // Refs for handling clicks outside dropdowns
  const departmentDropdownRef = useRef(null);
  const allowanceDropdownRef = useRef(null);
  const bonusDropdownRef = useRef(null);
  const deductionDropdownRef = useRef(null);

  // Calculate total compensation
  const calculateTotal = () => {
    const base = parseFloat(formData.baseSalary) || 0;
    const allowancesTotal = formData.allowances.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const bonusesTotal = formData.bonuses.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const deductionsTotal = formData.deductions.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    
    return {
      gross: base + allowancesTotal + bonusesTotal,
      net: base + allowancesTotal + bonusesTotal - deductionsTotal,
      allowancesTotal,
      bonusesTotal,
      deductionsTotal
    };
  };

  // Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};
  
  // Handle basic input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  // Handle dropdown toggle
  const toggleDropdown = (dropdown) => {
    setDropdowns({
      ...dropdowns,
      [dropdown]: !dropdowns[dropdown]
    });
  };

  // Select department from dropdown
  const selectDepartment = (dept) => {
    setFormData({
      ...formData,
      department: dept
    });
    setDropdowns({
      ...dropdowns,
      department: false
    });
    
    // Clear error for this field
    if (errors.department) {
      setErrors({
        ...errors,
        department: null
      });
    }
  };

  // Handle changes to allowances
  const handleAllowanceChange = (index, field, value) => {
    const updatedAllowances = [...formData.allowances];
    updatedAllowances[index][field] = value;
    setFormData({
      ...formData,
      allowances: updatedAllowances
    });
  };

  // Add a new allowance
  const addAllowance = () => {
    // Validate new allowance
    if (!newAllowance.type || newAllowance.amount <= 0) {
      setErrors({
        ...errors,
        newAllowance: 'Please enter a valid allowance type and amount'
      });
      return;
    }
    
    // Check if this type already exists
    if (formData.allowances.some(a => a.type === newAllowance.type)) {
      setErrors({
        ...errors,
        newAllowance: 'This allowance type already exists'
      });
      return;
    }
    
    setFormData({
      ...formData,
      allowances: [...formData.allowances, { ...newAllowance }]
    });
    
    setNewAllowance({ type: '', amount: 0 });
    setErrors({
      ...errors,
      newAllowance: null
    });
  };

  // Remove an allowance
  const removeAllowance = (index) => {
    const updatedAllowances = formData.allowances.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      allowances: updatedAllowances
    });
  };

  // Handle changes to bonuses
  const handleBonusChange = (index, field, value) => {
    const updatedBonuses = [...formData.bonuses];
    updatedBonuses[index][field] = value;
    setFormData({
      ...formData,
      bonuses: updatedBonuses
    });
  };

  // Add a new bonus
  const addBonus = () => {
    // Validate new bonus
    if (!newBonus.type || newBonus.amount <= 0) {
      setErrors({
        ...errors,
        newBonus: 'Please enter a valid bonus type and amount'
      });
      return;
    }
    
    // Check if this type already exists
    if (formData.bonuses.some(b => b.type === newBonus.type)) {
      setErrors({
        ...errors,
        newBonus: 'This bonus type already exists'
      });
      return;
    }
    
    setFormData({
      ...formData,
      bonuses: [...formData.bonuses, { ...newBonus }]
    });
    
    setNewBonus({ type: '', amount: 0 });
    setErrors({
      ...errors,
      newBonus: null
    });
  };

  // Remove a bonus
  const removeBonus = (index) => {
    const updatedBonuses = formData.bonuses.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      bonuses: updatedBonuses
    });
  };

  // Handle changes to deductions
  const handleDeductionChange = (index, field, value) => {
    const updatedDeductions = [...formData.deductions];
    updatedDeductions[index][field] = value;
    setFormData({
      ...formData,
      deductions: updatedDeductions
    });
  };

  // Add a new deduction
  const addDeduction = () => {
    // Validate new deduction
    if (!newDeduction.type || newDeduction.amount <= 0) {
      setErrors({
        ...errors,
        newDeduction: 'Please enter a valid deduction type and amount'
      });
      return;
    }
    
    // Check if this type already exists
    if (formData.deductions.some(d => d.type === newDeduction.type)) {
      setErrors({
        ...errors,
        newDeduction: 'This deduction type already exists'
      });
      return;
    }
    
    setFormData({
      ...formData,
      deductions: [...formData.deductions, { ...newDeduction }]
    });
    
    setNewDeduction({ type: '', amount: 0 });
    setErrors({
      ...errors,
      newDeduction: null
    });
  };

  // Remove a deduction
  const removeDeduction = (index) => {
    const updatedDeductions = formData.deductions.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      deductions: updatedDeductions
    });
  };

  // Form validation for the current step
  const validateCurrentStep = () => {
    const newErrors = {};
    
    if (currentStep === 1) {
      if (!formData.firstName) newErrors.firstName = 'First name is required';
      if (!formData.lastName) newErrors.lastName = 'Last name is required';
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
      }
      if (!formData.phone) newErrors.phone = 'Phone number is required';
      if (!formData.joiningDate) newErrors.joiningDate = 'Joining date is required';
    } else if (currentStep === 2) {
      if (!formData.department) newErrors.department = 'Department is required';
      if (!formData.position) newErrors.position = 'Position is required';
      if (!formData.baseSalary) {
        newErrors.baseSalary = 'Base salary is required';
      } else if (isNaN(parseFloat(formData.baseSalary)) || parseFloat(formData.baseSalary) <= 0) {
        newErrors.baseSalary = 'Please enter a valid amount';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Go to next step
  const nextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  // Go to previous step
  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  };

  // Prevent Enter key submission if not on the final step
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      // If the current element is a submit button, let it submit.
      if (e.target.type === 'submit') return;
      
      // Prevent default to avoid accidental submission.
      e.preventDefault();
      const form = e.target.form;
      const index = Array.prototype.indexOf.call(form, e.target);
      
      // If there is a next element, focus it. Otherwise, focus the submit button.
      if (form.elements[index + 1]) {
        form.elements[index + 1].focus();
      } else {
        const submitButton = form.querySelector('button[type="submit"]');
        if (submitButton) {
          submitButton.focus();
        }
      }
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const navigate = useNavigate();
//   const handleSubmitSuccess = (employeeData) => {
//     setSuccessData(employeeData);
//     setIsSuccessModalOpen(true);
//   };

  
  // Handle form submission only on the final step
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentStep < totalSteps) {
      return;
    }
    
    if (validateCurrentStep()) {
      const departmentValue = formData.department === 'Other' ? formData.customDepartment : formData.department;

      const submissionData = {
        email: formData.email,
        joiningDate: formData.joiningDate,
        baseSalary: parseFloat(formData.baseSalary),
        department: departmentValue,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phone,
        position: formData.position,
        allowances: formData.allowances.map(a => ({ type: a.type, amount: parseFloat(a.amount) })),
        bonuses: formData.bonuses.map(b => ({ type: b.type, amount: parseFloat(b.amount) })),
        deductions: formData.deductions.map(d => ({ type: d.type, amount: parseFloat(d.amount) }))
      };
      
      console.log('Submitting data:', submissionData);
      
      try {
        // Submit to the correct URL using the API service
        const response = await apiService.post('/api/staff-management/onboard', token, submissionData);
        console.log('Success:', response.data);
        setSuccessData(response.data);
        setIsSuccessModalOpen(true);
        toast.success('Employee onboarding data submitted successfully!');
      } catch (error) {
        console.error('Error:', error);
        toast.error('Failed to submit employee onboarding data.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Handlers for the modal actions:
    const handleAddAnother = () => {
    setIsSuccessModalOpen(false);
    // Reset the form to its initial state:
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      joiningDate: '',
      baseSalary: '',
      department: '',
      customDepartment: '',
      position: '',
      allowances: [
        { type: 'Housing', amount: 0 },
        { type: 'Transport', amount: 0 },
        { type: 'Medical', amount: 0 }
      ],
      bonuses: [
        { type: 'Performance', amount: 0 },
        { type: 'Year-End', amount: 0 }
      ],
      deductions: [
        { type: 'Tax', amount: 0 },
        { type: 'Pension', amount: 0 },
        { type: 'Health Insurance', amount: 0 }
      ]
    });
    // Optionally, navigate to the first step:
    setCurrentStep(1);
  };
  
  const handleViewAllEmployees = () => {
    setIsSuccessModalOpen(false);
    // Navigate to the employees list page (adjust the route as needed)
    navigate('/all-staff');
  };
  

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (departmentDropdownRef.current && !departmentDropdownRef.current.contains(event.target)) {
        setDropdowns(prev => ({ ...prev, department: false }));
      }
      if (allowanceDropdownRef.current && !allowanceDropdownRef.current.contains(event.target)) {
        setDropdowns(prev => ({ ...prev, allowanceType: false }));
      }
      if (bonusDropdownRef.current && !bonusDropdownRef.current.contains(event.target)) {
        setDropdowns(prev => ({ ...prev, bonusType: false }));
      }
      if (deductionDropdownRef.current && !deductionDropdownRef.current.contains(event.target)) {
        setDropdowns(prev => ({ ...prev, deductionType: false }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Total calculation
  const totals = calculateTotal();

  // Progress bar width based on current step
  const progressWidth = `${(currentStep / totalSteps) * 100}%`;

   
  return (
    <div className="flex min-h-screen bg-gray-50 source-sans-3 mt-4">
      <div className="flex-1 p-4 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-primary-800 source-sans-3">Employee Onboarding</h2>
              <p className="text-primary-600 mt-1 source-sans-3">Add a new employee to the system</p>
            </div>
            <div className="mt-4 sm:mt-0">
              <button 
                className="flex items-center py-2 px-3 bg-primary-50 hover:bg-primary-100 rounded-lg text-primary-700 text-sm font-medium"
                onClick={() => window.history.back()}
              >
                <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* Progress Tracker */}
        <div className="mb-8">
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div className="bg-primary-600 h-2.5 rounded-full transition-all duration-300" style={{ width: progressWidth }}></div>
          </div>
          <div className="flex justify-between mt-2">
            <div className={`text-sm ${currentStep >= 1 ? 'text-primary-700 font-medium' : 'text-gray-500'}`}>
              Personal Info
            </div>
            <div className={`text-sm ${currentStep >= 2 ? 'text-primary-700 font-medium' : 'text-gray-500'}`}>
              Job Details
            </div>
            <div className={`text-sm ${currentStep >= 3 ? 'text-primary-700 font-medium' : 'text-gray-500'}`}>
              Compensation
            </div>
          </div>
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-xl shadow-sm p-6 md:p-8">
          <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-6">Personal Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* First Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="firstName">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={`w-full p-3 border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500`}
                      placeholder="Enter first name"
                    />
                    {errors.firstName && <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>}
                  </div>
                  
                  {/* Last Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="lastName">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={`w-full p-3 border ${errors.lastName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500`}
                      placeholder="Enter last name"
                    />
                    {errors.lastName && <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>}
                  </div>
                  
                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full p-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500`}
                      placeholder="name@company.com"
                    />
                    {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                  </div>
                  
                  {/* Phone Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="phone">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full p-3 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500`}
                      placeholder="+1 (555) 000-0000"
                    />
                    {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
                  </div>
                  
                  {/* Joining Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="joiningDate">
                      Joining Date
                    </label>
                    <input
                      type="date"
                      id="joiningDate"
                      name="joiningDate"
                      value={formData.joiningDate}
                      onChange={handleInputChange}
                      className={`w-full p-3 border ${errors.joiningDate ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    />
                    {errors.joiningDate && <p className="mt-1 text-sm text-red-500">{errors.joiningDate}</p>}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Job Details */}
            {currentStep === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-6">Job Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Department Dropdown */}
                    <div className="relative" ref={departmentDropdownRef}>
                    <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-1">
                        Department
                    </label>
                    <div className={`w-full p-3 border ${errors.department ? 'border-red-500' : 'border-gray-300'} rounded-lg flex justify-between items-center cursor-pointer bg-white`}
                        onClick={() => toggleDropdown('department')}>
                        <span className={formData.department ? 'text-gray-900' : 'text-gray-400'}>
                        {formData.department || 'Select department'}
                        </span>
                        {/* Dropdown arrow SVG */}
                    </div>
                    {dropdowns.department && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {departmentOptions.map((dept, index) => (
                            <div key={index} className="p-3 hover:bg-primary-50 cursor-pointer text-gray-800"
                            onClick={() => selectDepartment(dept)}>
                            {dept}
                            </div>
                        ))}
                        <div className="p-3 hover:bg-primary-50 cursor-pointer text-gray-800"
                            onClick={() => selectDepartment('Other')}>
                            Other
                        </div>
                        </div>
                    )}
                    {errors.department && <p className="mt-1 text-sm text-red-500">{errors.department}</p>}
                    </div>

                    {/* Custom Department Input - rendered only when "Other" is selected */}
                    {formData.department === 'Other' && (
                    <div>
                        <label htmlFor="customDepartment" className="block text-sm font-medium text-gray-700 mb-1">
                        Custom Department
                        </label>
                        <input
                        type="text"
                        id="customDepartment"
                        name="customDepartment"
                        value={formData.customDepartment}
                        onChange={handleInputChange}
                        className={`w-full p-3 border ${errors.customDepartment ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500`}
                        placeholder="Enter your department"
                        />
                        {errors.customDepartment && <p className="mt-1 text-sm text-red-500">{errors.customDepartment}</p>}
                    </div>
                    )}

                  
                  {/* Position */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="position">
                      Position
                    </label>
                    <input
                      type="text"
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      className={`w-full p-3 border ${errors.position ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500`}
                      placeholder="Enter position"
                    />
                    {errors.position && <p className="mt-1 text-sm text-red-500">{errors.position}</p>}
                  </div>
                  
                  {/* Base Salary */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="baseSalary">
                      Base Salary
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        id="baseSalary"
                        name="baseSalary"
                        value={formData.baseSalary}
                        onChange={handleInputChange}
                        className={`w-full pl-7 p-3 border ${errors.baseSalary ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500`}
                        placeholder="0.00"
                        min="0"
                        // step="0.01"
                      />
                    </div>
                    {errors.baseSalary && <p className="mt-1 text-sm text-red-500">{errors.baseSalary}</p>}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Compensation */}
            {currentStep === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-6">Compensation & Benefits</h3>
                
                {/* Allowances Section */}
                <div className="mb-8">
                  <h4 className="text-md font-medium text-gray-700 mb-4">Allowances</h4>
                  
                  {/* Current Allowances */}
                  <div className="mb-4">
                    {formData.allowances.map((allowance, index) => (
                      <div key={index} className="flex items-center mb-3">
                        <div className="flex-1 mr-4">
                          <input
                            type="text"
                            value={allowance.type}
                            onChange={(e) => handleAllowanceChange(index, 'type', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="Allowance type"
                            readOnly
                          />
                        </div>
                        <div className="flex-1 mr-4">
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              value={allowance.amount}
                              onChange={(e) => handleAllowanceChange(index, 'amount', e.target.value)}
                              onBlur={(e) => {
                                const formattedValue = Number(e.target.value).toFixed(2);
                                handleAllowanceChange(index, 'amount', formattedValue);
                              }}
                              className="w-full pl-7 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="0.00"
                              min="0"
                            //   step="0.01"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAllowance(index)}
                          className="p-2 text-red-500 hover:text-red-700"
                        >
                          <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add New Allowance */}
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                    <div className="md:col-span-5 relative" ref={allowanceDropdownRef}>
                      <div 
                        className="w-full p-3 border border-gray-300 rounded-lg flex justify-between items-center cursor-pointer bg-white"
                        onClick={() => toggleDropdown('allowanceType')}
                      >
                        <span className={newAllowance.type ? 'text-gray-900' : 'text-gray-400'}>
                          {newAllowance.type || 'Select allowance type'}
                        </span>
                        <svg className={`w-5 h-5 text-gray-500 transform ${dropdowns.allowanceType ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                      
                      {dropdowns.allowanceType && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {allowanceOptions.map((type, index) => (
                            <div
                              key={index}
                              className="p-3 hover:bg-primary-50 cursor-pointer text-gray-800"
                              onClick={() => {
                                setNewAllowance({ ...newAllowance, type });
                                setDropdowns({ ...dropdowns, allowanceType: false });
                              }}
                            >
                              {type}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="md:col-span-5">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          value={newAllowance.amount}
                          onChange={(e) => setNewAllowance({ ...newAllowance, amount: e.target.value })}
                          className="w-full pl-7 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="0.00"
                          min="0"
                        //   step="0.01"
                        />
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <button
                        type="button"
                        onClick={addAllowance}
                        className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                      >
                        Add
                      </button>
                    </div>
                    {errors.newAllowance && <p className="md:col-span-12 mt-1 text-sm text-red-500">{errors.newAllowance}</p>}
                  </div>
                </div>

                {/* Bonuses Section */}
                <div className="mb-8">
                  <h4 className="text-md font-medium text-gray-700 mb-4">Bonuses</h4>
                  
                  {/* Current Bonuses */}
                  <div className="mb-4">
                    {formData.bonuses.map((bonus, index) => (
                      <div key={index} className="flex items-center mb-3">
                        <div className="flex-1 mr-4">
                          <input
                            type="text"
                            value={bonus.type}
                            onChange={(e) => handleBonusChange(index, 'type', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="Bonus type"
                            readOnly
                          />
                        </div>
                        <div className="flex-1 mr-4">
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              value={bonus.amount}
                              onChange={(e) => handleBonusChange(index, 'amount', e.target.value)}
                              onBlur={(e) => {
                                const formattedValue = Number(e.target.value).toFixed(2);
                                handleBonusChange(index, 'amount', formattedValue);
                              }}
                              className="w-full pl-7 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="0.00"
                              min="0"
                            //   step="0.01"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeBonus(index)}
                          className="p-2 text-red-500 hover:text-red-700"
                        >
                          <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add New Bonus */}
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                    <div className="md:col-span-5 relative" ref={bonusDropdownRef}>
                      <div 
                        className="w-full p-3 border border-gray-300 rounded-lg flex justify-between items-center cursor-pointer bg-white"
                        onClick={() => toggleDropdown('bonusType')}
                      >
                        <span className={newBonus.type ? 'text-gray-900' : 'text-gray-400'}>
                          {newBonus.type || 'Select bonus type'}
                        </span>
                        <svg className={`w-5 h-5 text-gray-500 transform ${dropdowns.bonusType ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                      
                      {dropdowns.bonusType && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {bonusOptions.map((type, index) => (
                            <div
                              key={index}
                              className="p-3 hover:bg-primary-50 cursor-pointer text-gray-800"
                              onClick={() => {
                                setNewBonus({ ...newBonus, type });
                                setDropdowns({ ...dropdowns, bonusType: false });
                              }}
                            >
                              {type}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="md:col-span-5">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          value={newBonus.amount}
                          onChange={(e) => setNewBonus({ ...newBonus, amount: e.target.value })}
                          className="w-full pl-7 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="0.00"
                          min="0"
                        //   step="0.01"
                        />
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <button
                        type="button"
                        onClick={addBonus}
                        className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                      >
                        Add
                      </button>
                    </div>
                    {errors.newBonus && <p className="md:col-span-12 mt-1 text-sm text-red-500">{errors.newBonus}</p>}
                  </div>
                </div>

                {/* Deductions Section */}
                <div className="mb-8">
                  <h4 className="text-md font-medium text-gray-700 mb-4">Deductions</h4>
                  
                  {/* Current Deductions */}
                  <div className="mb-4">
                    {formData.deductions.map((deduction, index) => (
                      <div key={index} className="flex items-center mb-3">
                        <div className="flex-1 mr-4">
                          <input
                            type="text"
                            value={deduction.type}
                            onChange={(e) => handleDeductionChange(index, 'type', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="Deduction type"
                            readOnly
                          />
                        </div>
                        <div className="flex-1 mr-4">
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              value={deduction.amount}
                              onChange={(e) => handleDeductionChange(index, 'amount', e.target.value)}
                              onBlur={(e) => {
                                const formattedValue = Number(e.target.value).toFixed(2);
                                handleDeductionChange(index, 'amount', formattedValue);
                              }}
                              className="w-full pl-7 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="0.00"
                              min="0"
                            //   step="0.01"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeDeduction(index)}
                          className="p-2 text-red-500 hover:text-red-700"
                        >
                          <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add New Deduction */}
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                    <div className="md:col-span-5 relative" ref={deductionDropdownRef}>
                      <div 
                        className="w-full p-3 border border-gray-300 rounded-lg flex justify-between items-center cursor-pointer bg-white"
                        onClick={() => toggleDropdown('deductionType')}
                      >
                        <span className={newDeduction.type ? 'text-gray-900' : 'text-gray-400'}>
                          {newDeduction.type || 'Select deduction type'}
                        </span>
                        <svg className={`w-5 h-5 text-gray-500 transform ${dropdowns.deductionType ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                      
                      {dropdowns.deductionType && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {deductionOptions.map((type, index) => (
                            <div
                              key={index}
                              className="p-3 hover:bg-primary-50 cursor-pointer text-gray-800"
                              onClick={() => {
                                setNewDeduction({ ...newDeduction, type });
                                setDropdowns({ ...dropdowns, deductionType: false });
                              }}
                            >
                              {type}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="md:col-span-5">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          value={newDeduction.amount}
                          onChange={(e) => setNewDeduction({ ...newDeduction, amount: e.target.value })}
                          className="w-full pl-7 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="0.00"
                          min="0"
                        //   step="0.01"
                        />
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <button
                        type="button"
                        onClick={addDeduction}
                        className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                      >
                        Add
                      </button>
                    </div>
                    {errors.newDeduction && <p className="md:col-span-12 mt-1 text-sm text-red-500">{errors.newDeduction}</p>}
                  </div>
                </div>

                {/* Compensation Summary */}
                <div className="bg-gray-50 p-6 rounded-lg mb-6">
                  <h4 className="text-md font-medium text-gray-700 mb-4">Compensation Summary</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-600">Base Salary:</span>
                        <span className="font-medium">{formatCurrency(parseFloat(formData.baseSalary) || 0)}</span>
                      </div>
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-600">Total Allowances:</span>
                        <span className="font-medium">{formatCurrency(totals.allowancesTotal)}</span>
                      </div>
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-600">Total Bonuses:</span>
                        <span className="font-medium">{formatCurrency(totals.bonusesTotal)}</span>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-600">Total Deductions:</span>
                        <span className="font-medium text-red-600">- {formatCurrency(totals.deductionsTotal)}</span>
                      </div>
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-600">Gross Compensation:</span>
                        <span className="font-medium">{formatCurrency(totals.gross)}</span>
                      </div>
                      <div className="flex justify-between mb-2 pt-2 border-t border-gray-200">
                        <span className="text-gray-800 font-medium">Net Compensation:</span>
                        <span className="font-semibold text-lg text-primary-700">{formatCurrency(totals.net)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Form Navigation Buttons */}
            <div className="flex justify-between mt-8">
              {currentStep > 1 ? (
                <button
                  type="button"
                  onClick={prevStep}
                  className="py-3 px-6 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-medium transition-colors"
                >
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Previous
                  </span>
                </button>
              ) : (
                <div></div> // Empty div for spacing when no back button
              )}
              
              {currentStep < totalSteps ? (
                <button
                    type="button"
                    onClick={nextStep}
                    className="py-3 px-6 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                >
                    <span className="flex items-center">
                    Next
                    <svg className="w-4 h-4 ml-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    </span>
                </button>
                ) : (
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="py-3 px-6 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <span className="flex items-center">
                    {isSubmitting ? (
                        <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M5 13l4 4L19 7" />
                        </svg>
                        Submitting...
                        </>
                    ) : (
                        <>
                        Submit
                        <svg className="w-4 h-4 ml-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        </>
                    )}
                    </span>
                </button>
                )}
            </div>
          </form>
        </div>
      </div>

      {/* Right Sidebar */}
      <div className="hidden lg:block lg:w-80 p-8 bg-gray-50 border-l border-gray-200">
        <div className="sticky top-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Employee Summary</h3>
          
          {/* Step 1 Summary */}
          <div className={`mb-6 ${currentStep > 1 ? '' : 'opacity-50'}`}>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Personal Information</h4>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-gray-800 font-medium">{formData.firstName} {formData.lastName}</p>
              {formData.email && (
                <p className="text-gray-600 text-sm mt-1">
                  <span className="inline-block mr-1">
                    <svg className="w-4 h-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </span>
                  {formData.email}
                </p>
              )}
              {formData.phone && (
                <p className="text-gray-600 text-sm mt-1">
                  <span className="inline-block mr-1">
                    <svg className="w-4 h-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </span>
                  {formData.phone}
                </p>
              )}
              {formData.joiningDate && (
                <p className="text-gray-600 text-sm mt-1">
                  <span className="inline-block mr-1">
                    <svg className="w-4 h-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </span>
                  Joining: {new Date(formData.joiningDate).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
          
          {/* Step 2 Summary */}
          <div className={`mb-6 ${currentStep > 2 ? '' : 'opacity-50'}`}>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Job Details</h4>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              {formData.position && (
                <p className="text-gray-800 font-medium">{formData.position}</p>
              )}
              {formData.department && (
                <p className="text-gray-600 text-sm mt-1">
                  <span className="inline-block mr-1">
                    <svg className="w-4 h-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </span>
                  {formData.department === 'Other' ? formData.customDepartment : formData.department}
                </p>
              )}
              {formData.baseSalary && (
                <p className="text-gray-600 text-sm mt-1">
                  <span className="inline-block mr-1">
                    <svg className="w-4 h-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                  Base Salary: {formatCurrency(parseFloat(formData.baseSalary) || 0)}
                </p>
              )}
            </div>
          </div>
          
          {/* Step 3 Summary */}
          <div className={`${currentStep > 3 ? '' : 'opacity-50'}`}>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Compensation</h4>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600 text-sm">Gross:</span>
                <span className="text-gray-800 font-medium">{formatCurrency(totals.gross)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 text-sm">Net:</span>
                <span className="text-primary-700 font-medium">{formatCurrency(totals.net)}</span>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex justify-between mb-1">
                  <span className="text-gray-600 text-xs">Allowances:</span>
                  <span className="text-gray-800 text-xs">{formatCurrency(totals.allowancesTotal)}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-600 text-xs">Bonuses:</span>
                  <span className="text-gray-800 text-xs">{formatCurrency(totals.bonusesTotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 text-xs">Deductions:</span>
                  <span className="text-red-600 text-xs">- {formatCurrency(totals.deductionsTotal)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {
            isSuccessModalOpen && (
                <SuccessModal
                isOpen={isSuccessModalOpen}
                onClose={() => setIsSuccessModalOpen(false)}
                employeeData={successData}
                onAddAnother={handleAddAnother}
                onViewAllEmployees={handleViewAllEmployees}
                />
            )
        }
    </div>
  );
};

export default EmployeeOnboarding;
